// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Dropdown,
  Empty,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { DeleteOutlined, MoreOutlined, ThunderboltOutlined, UsergroupAddOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import type { BulkProspectCandidate } from '../../../../state_manager/data_models/bulk_prospect_candidates_model.ts'
import type { ProspectRecord } from '../../../../state_manager/interfaces/fundraising.ts'
import { useBulkAddProspects, useFundProspects } from '../../hooks/useBulkAddProspects.ts'
import {
  patchPipelineProspectStage,
  patchProspectCommitment,
  deleteProspect
} from '../../services/fundraisingService.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { getDefaultPipelineConfigForPipeline } from '../../utils/pipelineFundStageRules.ts'
import { PIPELINE_TYPE_FUNDRAISING } from '../../../../state_manager/globals.ts'
import { FundSelect } from '../../../../core/components/FundSelect.tsx'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { useNumberFormat } from '../../../../core/utils/inputNumberFormat.ts'

const { Title, Text } = Typography

/** One editable grid row: the resolved candidate + this session's edits.
 * `included` (not the table's row selection) decides what gets created —
 * selection is purely for bulk actions. `created` marks rows already
 * created in this session (the screen stays open after a create). */
type CandidateRow = BulkProspectCandidate & {
  stageId: string | null
  commitment: number | null
  included: boolean
  created: boolean
}

interface FilterFormValues {
  target_fund_id?: string
  fund_ids: string[]
  lp_status_ids: string[]
}

const EMPTY_FORM: FilterFormValues = {
  target_fund_id: undefined,
  fund_ids: [],
  lp_status_ids: []
}

export default function BulkAddProspectsPage() {
  const { candidates, resolving, creating, resolve, create, clear } = useBulkAddProspects()
  const [form] = Form.useForm<FilterFormValues>()
  const [modal, modalContextHolder] = Modal.useModal()

  // Option universes — all already in the globalConfigs slice (no refetch).
  const funds = useSelector((state: any) => state.globalConfigs?.funds || [])
  const pipelines = useSelector((state: any) => state.globalConfigs?.pipelines || [])
  const pipelineConfigs = useSelector((state: any) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: any) => state.globalConfigs?.pipeline_stages || [])
  const lpStatuses = useSelector((state: any) => state.globalConfigs?.comms_lp_statuses || [])
  const pipelineOutcomes = useSelector((state: any) => state.globalConfigs?.pipeline_outcomes || [])
  const pipelineStatuses = useSelector((state: any) => state.globalConfigs?.pipeline_statuses || [])

  // "Closed" is special: a closed prospect is locked against edits here, and
  // Closed is never offered as a bulk-set target (deliberate per-row sets
  // remain possible). Interim guard until role rights protect this properly.
  //
  // It is now asked for by MEANING, not by name: the fundraising status whose
  // outcome is `invested`. This used to match the machine key
  // 'fundraising_status_060' with a `label === 'Closed'` fallback — the sole
  // behavioural reader of cfg.pipeline_statuses.value, and the reason that
  // fossil column survived as long as it did. Rename the status or the outcome
  // and this keeps working; only the outcome_key is contractual.
  const closedDecisionStatusId = useMemo(() => {
    const investedOutcomeIds = new Set(
      (pipelineOutcomes as any[])
        .filter((o) => o?.pipeline_type_key === 'fundraising' && o?.outcome_key === 'invested')
        .map((o) => String(o.id))
    )
    if (investedOutcomeIds.size === 0) return null
    const status = (pipelineStatuses as any[]).find(
      (s) => s?.outcome_id && investedOutcomeIds.has(String(s.outcome_id)) && s?.is_terminal
    )
    return status?.id ?? null
  }, [pipelineOutcomes, pipelineStatuses])

  const closedStageIds = useMemo(() => {
    if (!closedDecisionStatusId) return new Set<string>()
    return new Set<string>(
      pipelineStages
        .filter((s: any) => s?.decision_status === closedDecisionStatusId)
        .map((s: any) => String(s.id))
    )
  }, [pipelineStages, closedDecisionStatusId])

  const closedConfigIds = useMemo(
    () =>
      new Set<string>(
        pipelineConfigs
          .filter((c: any) => c && closedStageIds.has(String(c.stage)))
          .map((c: any) => String(c.id))
      ),
    [pipelineConfigs, closedStageIds]
  )

  const { getCurrencyById } = useCurrencies()
  const currencyFmt = useNumberFormat('currency')

  // Session-only editable rows built from the resolved candidates; the
  // rowSelection checkboxes are the include/exclude mechanism.
  const [rows, setRows] = useState<CandidateRow[] | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkStageId, setBulkStageId] = useState<string | undefined>(undefined)

  const watchedTargetFundId = Form.useWatch('target_fund_id', form)
  const {
    prospects: fundProspects,
    loading: fundProspectsLoading,
    loadingMore: fundProspectsLoadingMore,
    hasMore: fundProspectsHasMore,
    refetch: refetchFundProspects,
    loadMore: loadMoreFundProspects,
    updateLocal: updateFundProspectLocal
  } = useFundProspects(watchedTargetFundId)

  // Fund picker gate (fund status, 2026-09-04). The TARGET fund is the fund you
  // are raising into, so scope 'fundraising' — a closed raise does not belong
  // in the list. FundSelect owns the gate and the "show all" way back.
  //
  // `filterFundOptions` below stays UNGATED on purpose: it is the historical
  // "LPs in funds" filter, and a fund you no longer raise for is exactly the
  // kind of thing you filter an LP base by.
  const filterFundOptions = useMemo(
    () =>
      funds
        .filter((f: any) => f && f.id)
        .map((f: any) => ({ value: f.id, label: f.name || '' }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
    [funds]
  )
  const lpStatusOptions = useMemo(
    () =>
      [...lpStatuses]
        .sort((a: any, b: any) => (a.precedence ?? 0) - (b.precedence ?? 0))
        .map((s: any) => ({ value: s.id, label: s.label || '' })),
    [lpStatuses]
  )

  const defaultFundraisingPipelineId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (p: { default?: boolean; type?: string; id?: string }) =>
        p.default === true && p.type === PIPELINE_TYPE_FUNDRAISING
    ) as { id?: string } | undefined
    return defaultPipeline?.id || null
  }, [pipelines])

  const targetFundRow = useMemo(
    () => funds.find((f: any) => f?.id === watchedTargetFundId),
    [funds, watchedTargetFundId]
  )

  // Fund's own origination pipeline → default Fundraising pipeline (the same
  // resolution order as the drawer/kanban; server re-derives independently).
  const effectivePipelineId: string | null =
    (typeof targetFundRow?.pipeline_origination === 'string' && targetFundRow.pipeline_origination) ||
    defaultFundraisingPipelineId

  const defaultStage = useMemo(
    () => getDefaultPipelineConfigForPipeline(effectivePipelineId, pipelineConfigs, pipelineStages),
    [effectivePipelineId, pipelineConfigs, pipelineStages]
  )

  const stageOptions = useMemo(() => {
    if (!effectivePipelineId) return []
    const seqKey = (c: any) =>
      typeof c.sequence_tbl === 'number' && !Number.isNaN(c.sequence_tbl)
        ? c.sequence_tbl
        : Number.POSITIVE_INFINITY
    return pipelineConfigs
      .filter(
        (c: any) => String(c.pipeline) === String(effectivePipelineId) && c.visible_tbl === true
      )
      .sort((a: any, b: any) => seqKey(a) - seqKey(b))
      .map((c: any) => ({
        value: c.id,
        label: (pipelineStages.find((s: any) => s.id === c.stage)?.name as string) || ''
      }))
  }, [effectivePipelineId, pipelineConfigs, pipelineStages])

  // Bulk-set targets exclude Closed (per-row selects keep the full list).
  const bulkStageOptions = useMemo(
    () => stageOptions.filter((o: any) => !closedConfigIds.has(String(o.value))),
    [stageOptions, closedConfigIds]
  )

  const fundCurrency = useMemo(() => {
    const currencyId = targetFundRow?.functional_currency_id
    return currencyId ? getCurrencyById(String(currencyId)) : null
  }, [targetFundRow, getCurrencyById])

  const handleGenerate = useCallback(async () => {
    let values: FilterFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    const err = await resolve({
      target_fund_id: values.target_fund_id as string,
      fund_ids: values.fund_ids ?? [],
      lp_status_ids: values.lp_status_ids ?? []
    })
    if (err) {
      showErrorToast(err)
      return
    }
  }, [form, resolve])

  // Build the editable rows whenever a fresh resolution lands: default stage
  // prefilled, flagged (already-in-pipeline) rows unticked by default.
  React.useEffect(() => {
    if (candidates == null) {
      setRows(null)
      setSelectedRowKeys([])
      return
    }
    setRows(
      candidates.map(
        (c): CandidateRow => ({
          ...c,
          stageId: defaultStage?.configId ?? null,
          commitment: null,
          included: !c.prog__has_live_prospect_in_target,
          created: false
        })
      )
    )
    // Selection is purely for bulk actions — start empty.
    setSelectedRowKeys([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates])

  // Changing the target fund invalidates every per-row stage (stage ids
  // belong to the old fund's pipeline) — confirm, then drop the grid.
  const handleTargetFundChange = useCallback(
    (nextFundId: string | undefined) => {
      if (rows && rows.length > 0) {
        modal.confirm({
          title: 'Discard the resolved list?',
          content:
            'Changing the target fund changes the pipeline, so the resolved list and its stage edits will be cleared.',
          okText: 'Change fund',
          onOk: () => {
            form.setFieldsValue({ target_fund_id: nextFundId })
            clear()
          },
          onCancel: () => {
            // revert the select to the previous value
            form.setFieldsValue({ target_fund_id: watchedTargetFundId })
          }
        })
      } else {
        form.setFieldsValue({ target_fund_id: nextFundId })
      }
    },
    [rows, modal, form, clear, watchedTargetFundId]
  )

  const updateRow = useCallback((accountId: string, patch: Partial<CandidateRow>) => {
    setRows((prev) =>
      prev ? prev.map((r) => (r.accounts__id === accountId ? { ...r, ...patch } : r)) : prev
    )
  }, [])

  const removeSelected = useCallback(() => {
    const selected = new Set(selectedRowKeys.map(String))
    setRows((prev) => (prev ? prev.filter((r) => !selected.has(r.accounts__id)) : prev))
    setSelectedRowKeys([])
  }, [selectedRowKeys])

  const applyBulkStage = useCallback(() => {
    if (!bulkStageId) return
    const selected = new Set(selectedRowKeys.map(String))
    setRows((prev) =>
      prev
        ? prev.map((r) => (selected.has(r.accounts__id) ? { ...r, stageId: bulkStageId } : r))
        : prev
    )
  }, [bulkStageId, selectedRowKeys])

  // Bulk include/exclude — flips only `included` (created rows stay out).
  const setIncludedForSelected = useCallback(
    (included: boolean) => {
      const selected = new Set(selectedRowKeys.map(String))
      setRows((prev) =>
        prev
          ? prev.map((r) =>
              selected.has(r.accounts__id) && !r.created ? { ...r, included } : r
            )
          : prev
      )
    },
    [selectedRowKeys]
  )

  const handleCreate = useCallback(async () => {
    if (!rows || !watchedTargetFundId) return
    const included = rows.filter((r) => r.included && !r.created)
    if (!included.length) return

    const missingStage = included.filter((r) => !r.stageId)
    if (missingStage.length) {
      showErrorToast('Every included LP needs a stage before creating prospects.')
      return
    }

    const res = await create({
      target_fund_id: watchedTargetFundId,
      rows: included.map((r) => ({
        investor_id: r.accounts__id,
        stage_id: r.stageId as string,
        ...(r.commitment != null ? { commitment: r.commitment } : {}),
        ...(r.prog__has_live_prospect_in_target ? { allow_duplicate: true } : {})
      }))
    })

    if (res.error) {
      showErrorToast(res.error)
      if (res.conflictAccountIds?.length) {
        // Refresh the flags so the conflicting rows show as already-in-pipeline.
        await handleGenerate()
        refetchFundProspects()
      }
      return
    }

    toast.success(`Created ${res.data?.created_count ?? included.length} prospects.`)
    // The screen stays open: mark the created rows (green tag, out of any
    // further create), keep filters and remaining edits intact, and refresh
    // the existing-prospects list below so the new rows appear there.
    const createdIds = new Set(included.map((r) => r.accounts__id))
    setRows((prev) =>
      prev
        ? prev.map((r) =>
            createdIds.has(r.accounts__id) ? { ...r, created: true, included: false } : r
          )
        : prev
    )
    setSelectedRowKeys((prev) => prev.filter((k) => !createdIds.has(String(k))))
    refetchFundProspects()
  }, [rows, watchedTargetFundId, create, handleGenerate, refetchFundProspects])

  const selectedCount = selectedRowKeys.length
  const includedCount = useMemo(
    () => (rows ? rows.filter((r) => r.included && !r.created).length : 0),
    [rows]
  )

  const columns: ColumnsType<CandidateRow> = useMemo(
    () => [
      {
        title: 'Include',
        key: 'included',
        width: 80,
        align: 'center' as const,
        render: (_: unknown, row: CandidateRow) =>
          row.created ? (
            <Tag color='green'>Created</Tag>
          ) : (
            <Checkbox
              checked={row.included}
              onChange={(e) => updateRow(row.accounts__id, { included: e.target.checked })}
            />
          )
      },
      {
        title: 'LP',
        dataIndex: 'accounts__name',
        key: 'accounts__name',
        ellipsis: true,
        // Explicit floor: without it the flexible column collapses to zero
        // when the fixed-width columns overflow a narrow viewport.
        width: 200,
        render: (name: string | null | undefined) => <Text>{name || '—'}</Text>
      },
      {
        title: 'LP Status',
        dataIndex: 'lp_status_label',
        key: 'lp_status_label',
        width: 110,
        render: (label: string | null | undefined) => (label ? <Tag>{label}</Tag> : null)
      },
      {
        title: '',
        key: 'flag',
        width: 150,
        render: (_: unknown, row: CandidateRow) =>
          row.prog__has_live_prospect_in_target ? (
            <Tooltip
              title={
                row.prog__existing_stage_label
                  ? `Live prospect in this fund — currently in "${row.prog__existing_stage_label}". Tick the row to create another one anyway.`
                  : 'Live prospect in this fund. Tick the row to create another one anyway.'
              }
            >
              <Tag color='orange'>Already in pipeline</Tag>
            </Tooltip>
          ) : null
      },
      {
        title: 'Stage',
        key: 'stage',
        width: 190,
        render: (_: unknown, row: CandidateRow) => (
          <Select
            size='small'
            className='w-full'
            value={row.stageId ?? undefined}
            options={stageOptions}
            placeholder='Select stage'
            disabled={row.created}
            onChange={(v) => updateRow(row.accounts__id, { stageId: v as string })}
          />
        )
      },
      {
        title: `Commitment${fundCurrency?.symbol ? ` (${fundCurrency.symbol})` : ''}`,
        key: 'commitment',
        width: 160,
        align: 'right' as const,
        render: (_: unknown, row: CandidateRow) => (
          <InputNumber
            size='small'
            className='w-full'
            value={row.commitment}
            {...currencyFmt}
            min={0}
            placeholder='Optional'
            disabled={row.created}
            onChange={(v) =>
              updateRow(row.accounts__id, { commitment: v == null ? null : Number(v) })
            }
          />
        )
      }
    ],
    [stageOptions, fundCurrency, currencyFmt, updateRow]
  )

  // Stage options per pipeline — existing prospects can sit on different
  // fundraising pipelines (e.g. SPV Fundraising), so each row's select is
  // scoped to its own pipeline's table-visible configs.
  const stageOptionsByPipeline = useMemo(() => {
    const map = new Map<string, { value: string; label: string }[]>()
    const seqKey = (c: any) =>
      typeof c.sequence_tbl === 'number' && !Number.isNaN(c.sequence_tbl)
        ? c.sequence_tbl
        : Number.POSITIVE_INFINITY
    for (const c of pipelineConfigs) {
      if (!c || c.visible_tbl !== true || !c.pipeline) continue
      const key = String(c.pipeline)
      if (!map.has(key)) map.set(key, [])
    }
    for (const [key] of map) {
      map.set(
        key,
        pipelineConfigs
          .filter((c: any) => String(c.pipeline) === key && c.visible_tbl === true)
          .sort((a: any, b: any) => seqKey(a) - seqKey(b))
          .map((c: any) => ({
            value: c.id,
            label: (pipelineStages.find((s: any) => s.id === c.stage)?.name as string) || ''
          }))
      )
    }
    return map
  }, [pipelineConfigs, pipelineStages])

  // Inline edits on EXISTING prospects: optimistic local merge, revert by
  // refetch on failure. Same endpoints the kanban drag / prospect form use.
  const handleExistingStageChange = useCallback(
    async (row: any, stageConfigId: string) => {
      const prospectId = String(row.prospects__id)
      const pipelineProspectsId = row.pipeline_prospects__id
      if (!pipelineProspectsId) {
        showErrorToast('This prospect has no pipeline placement to update.')
        return
      }
      const label =
        pipelineConfigs
          .filter((c: any) => c.id === stageConfigId)
          .map((c: any) => pipelineStages.find((s: any) => s.id === c.stage)?.name)[0] || ''
      updateFundProspectLocal(prospectId, {
        pipeline_prospects__stage: stageConfigId,
        pipeline_prospects__stage__label: label
      })
      const token = getAccessToken(store.getState().auth)
      const res = await patchPipelineProspectStage(String(pipelineProspectsId), stageConfigId, token)
      if (!res.success) {
        showErrorToast(res.error || 'Failed to update stage')
        refetchFundProspects()
      }
    },
    [pipelineConfigs, pipelineStages, updateFundProspectLocal, refetchFundProspects]
  )

  // Commitment saves on blur/Enter, only when actually changed.
  const dirtyCommitmentsRef = useRef<Set<string>>(new Set())
  const handleExistingCommitmentChange = useCallback(
    (row: any, value: number | null) => {
      const prospectId = String(row.prospects__id)
      dirtyCommitmentsRef.current.add(prospectId)
      updateFundProspectLocal(prospectId, { prospects__commitment: value })
    },
    [updateFundProspectLocal]
  )
  const handleExistingCommitmentSave = useCallback(
    async (row: any) => {
      const prospectId = String(row.prospects__id)
      if (!dirtyCommitmentsRef.current.has(prospectId)) return
      dirtyCommitmentsRef.current.delete(prospectId)
      const token = getAccessToken(store.getState().auth)
      const value = row.prospects__commitment == null ? null : Number(row.prospects__commitment)
      const res = await patchProspectCommitment(prospectId, value, token)
      if (!res.success) {
        showErrorToast(res.error || 'Failed to update commitment')
        refetchFundProspects()
      }
    },
    [refetchFundProspects]
  )

  // A closed prospect is locked: no inline edits, no bulk selection.
  const isExistingRowLocked = useCallback(
    (row: any) => closedConfigIds.has(String(row.pipeline_prospects__stage)),
    [closedConfigIds]
  )

  // Bulk edits on the existing list: selection + set-stage (Closed excluded).
  const [existingSelectedKeys, setExistingSelectedKeys] = useState<React.Key[]>([])
  const [existingBulkStageId, setExistingBulkStageId] = useState<string | undefined>(undefined)

  const applyExistingBulkStage = useCallback(async () => {
    if (!existingBulkStageId) return
    const config = pipelineConfigs.find((c: any) => c.id === existingBulkStageId)
    if (!config) return
    const selected = new Set(existingSelectedKeys.map(String))
    const targets = fundProspects.filter((p: any) => {
      if (!selected.has(String(p.prospects__id))) return false
      if (isExistingRowLocked(p)) return false
      if (!p.pipeline_prospects__id) return false
      // The stage config must belong to the row's own pipeline.
      const rowPipeline =
        (typeof p.prospects__pipeline_origination === 'string' && p.prospects__pipeline_origination) ||
        effectivePipelineId
      return String(config.pipeline) === String(rowPipeline)
    })
    const skipped = existingSelectedKeys.length - targets.length
    if (!targets.length) {
      toast.warn('None of the selected prospects can take that stage.')
      return
    }
    const label =
      (pipelineStages.find((s: any) => s.id === config.stage)?.name as string) || ''
    targets.forEach((p: any) =>
      updateFundProspectLocal(String(p.prospects__id), {
        pipeline_prospects__stage: existingBulkStageId,
        pipeline_prospects__stage__label: label
      })
    )
    const token = getAccessToken(store.getState().auth)
    const results = await Promise.all(
      targets.map((p: any) =>
        patchPipelineProspectStage(String(p.pipeline_prospects__id), existingBulkStageId, token)
      )
    )
    const failures = results.filter((r) => !r.success).length
    if (failures) {
      showErrorToast(`${failures} stage update${failures === 1 ? '' : 's'} failed.`)
      refetchFundProspects()
    } else {
      toast.success(
        `Updated ${targets.length} prospect${targets.length === 1 ? '' : 's'}` +
          (skipped > 0 ? ` (${skipped} skipped)` : '')
      )
    }
  }, [
    existingBulkStageId,
    existingSelectedKeys,
    fundProspects,
    pipelineConfigs,
    pipelineStages,
    effectivePipelineId,
    isExistingRowLocked,
    updateFundProspectLocal,
    refetchFundProspects
  ])

  // Delete lives under the bulk bar's ellipsis menu only (the Security
  // full-form convention: destructive actions behind MoreOutlined) and uses
  // the standard soft-delete cascade (DELETE /prospects/:id). Closed
  // prospects can't be selected, so they can't be deleted here.
  const deleteSelectedProspects = useCallback(() => {
    const selected = new Set(existingSelectedKeys.map(String))
    const targets = fundProspects.filter(
      (p: any) => selected.has(String(p.prospects__id)) && !isExistingRowLocked(p)
    )
    if (!targets.length) return
    modal.confirm({
      title: `Delete ${targets.length} prospect${targets.length === 1 ? '' : 's'}?`,
      content:
        'The selected prospects will be removed from this fund’s pipeline, together with their pipeline history.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        const token = getAccessToken(store.getState().auth)
        const results = await Promise.all(
          targets.map((p: any) =>
            deleteProspect(String(p.prospects__id), token).then(
              () => true,
              () => false
            )
          )
        )
        const failures = results.filter((ok) => !ok).length
        if (failures) {
          showErrorToast(`${failures} delete${failures === 1 ? '' : 's'} failed.`)
        } else {
          toast.success(`Deleted ${targets.length} prospect${targets.length === 1 ? '' : 's'}.`)
        }
        setExistingSelectedKeys([])
        refetchFundProspects()
      }
    })
  }, [existingSelectedKeys, fundProspects, isExistingRowLocked, modal, refetchFundProspects])

  // Context list of the target fund's existing live prospects — stage and
  // commitment are editable in place.
  const fundProspectColumns: ColumnsType<ProspectRecord> = useMemo(
    () => [
      {
        title: 'Investor',
        dataIndex: 'accounts__name',
        key: 'accounts__name',
        ellipsis: true,
        width: 200,
        render: (name: unknown) => <Text>{(name as string) || '—'}</Text>
      },
      {
        title: 'Stage',
        key: 'stage',
        width: 190,
        render: (_: unknown, row: any) => {
          if (isExistingRowLocked(row)) {
            return (
              <Tooltip title='Closed prospects are locked until role rights land.'>
                <Tag>{(row.pipeline_prospects__stage__label as string) || 'Closed'}</Tag>
              </Tooltip>
            )
          }
          const pipelineId =
            (typeof row.prospects__pipeline_origination === 'string' &&
              row.prospects__pipeline_origination) ||
            effectivePipelineId
          const options = (pipelineId && stageOptionsByPipeline.get(String(pipelineId))) || []
          return (
            <Select
              size='small'
              className='w-full'
              value={(row.pipeline_prospects__stage as string) ?? undefined}
              options={options}
              placeholder='Select stage'
              disabled={!row.pipeline_prospects__id}
              onChange={(v) => handleExistingStageChange(row, v as string)}
            />
          )
        }
      },
      {
        title: `Commitment${fundCurrency?.symbol ? ` (${fundCurrency.symbol})` : ''}`,
        key: 'commitment',
        width: 160,
        align: 'right' as const,
        render: (_: unknown, row: any) => (
          <InputNumber
            size='small'
            className='w-full'
            value={row.prospects__commitment == null ? null : Number(row.prospects__commitment)}
            {...currencyFmt}
            min={0}
            placeholder='—'
            disabled={isExistingRowLocked(row)}
            onChange={(v) => handleExistingCommitmentChange(row, v == null ? null : Number(v))}
            onBlur={() => handleExistingCommitmentSave(row)}
            onPressEnter={() => handleExistingCommitmentSave(row)}
          />
        )
      }
    ],
    [
      fundCurrency,
      currencyFmt,
      effectivePipelineId,
      stageOptionsByPipeline,
      isExistingRowLocked,
      handleExistingStageChange,
      handleExistingCommitmentChange,
      handleExistingCommitmentSave
    ]
  )

  // Continuous scroll for the existing-prospects table: the AntD scroll body
  // is the scroll container; append the next page near the bottom.
  const existingTableWrapRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const wrap = existingTableWrapRef.current
    if (!wrap) return
    const body = wrap.querySelector('.ant-table-body')
    if (!body) return
    const onScroll = () => {
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - 120) {
        loadMoreFundProspects()
      }
    }
    body.addEventListener('scroll', onScroll)
    return () => body.removeEventListener('scroll', onScroll)
  }, [loadMoreFundProspects, watchedTargetFundId, fundProspects.length])

  return (
    <div className='h-full overflow-auto bg-white'>
      <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
        {modalContextHolder}
        <Title level={4} style={{ marginTop: 0 }}>
          Bulk Prospects
        </Title>

        <Alert
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
          message='Create and manage prospects for existing LPs in bulk'
          description='Pick the fund you are raising for, then narrow the LP base by the funds they invested in and their LP status. Generate resolves the candidate list; rows with Include ticked are created as prospects in the target fund. Row-selection checkboxes are for bulk actions only. The fund&apos;s existing prospects below can be edited in place or restaged in bulk — closed prospects are locked, and Closed is never a bulk-set target.'
        />

        <Form<FilterFormValues> form={form} layout='vertical' initialValues={EMPTY_FORM} autoComplete='off'>
          <Row gutter={16}>
            <Col xs={24} md={10}>
              <Card title='Target fund' style={{ marginBottom: 16 }}>
                <Form.Item
                  name='target_fund_id'
                  label='Create prospects for fund'
                  rules={[{ required: true, message: 'A target fund is required' }]}
                  tooltip='Prospects are created in this fund. Its fundraising pipeline provides the stage list and the default stage.'
                >
                  <FundSelect
                    scope='fundraising'
                    onlyManagedByUs
                    allowClear
                    showSearch
                    placeholder='Select the fund being raised'
                    onChange={(v) => handleTargetFundChange(v as string | undefined)}
                  />
                </Form.Item>
                {watchedTargetFundId && (
                  <Text type='secondary'>
                    Default stage: {defaultStage?.label || '—'}
                    {fundCurrency?.symbol ? ` · Currency: ${fundCurrency.symbol}` : ''}
                  </Text>
                )}
              </Card>
            </Col>
            <Col xs={24} md={14}>
              <Card title='LP filters' style={{ marginBottom: 16 }}>
                <Form.Item
                  name='fund_ids'
                  label='LPs in funds'
                  tooltip='Empty = every LP. With funds selected (and no LP status), LPs with any real status in those funds qualify.'
                >
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='Any fund'
                    options={filterFundOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
                <Form.Item
                  name='lp_status_ids'
                  label='LP statuses'
                  tooltip='Statuses are computed within the selected funds (or across all funds when none are selected).'
                >
                  <Select
                    mode='multiple'
                    allowClear
                    showSearch
                    placeholder='Any real LP status'
                    options={lpStatusOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Space style={{ marginBottom: 16 }}>
            <Button
              type='primary'
              icon={<ThunderboltOutlined />}
              loading={resolving}
              onClick={handleGenerate}
            >
              Generate
            </Button>
            {rows != null && (
              <Text type='secondary'>
                {rows.length} candidate LPs · {includedCount} included
                {selectedCount > 0 ? ` · ${selectedCount} selected` : ''}
              </Text>
            )}
          </Space>
        </Form>

        {rows == null ? (
          <Empty description='Pick a target fund and Generate to resolve the candidate LPs.' />
        ) : (
          <Card
            title='Candidate LPs'
            style={{ marginBottom: 16 }}
            extra={
              <Button
                type='primary'
                icon={<UsergroupAddOutlined />}
                loading={creating}
                disabled={includedCount === 0}
                onClick={handleCreate}
              >
                Create {includedCount} prospect{includedCount === 1 ? '' : 's'}
              </Button>
            }
          >
            {selectedCount > 0 && (
              <Space style={{ marginBottom: 12 }} wrap>
                <Text strong>{selectedCount} selected:</Text>
                <Space.Compact>
                  <Button size='small' onClick={() => setIncludedForSelected(true)}>
                    Include
                  </Button>
                  <Button size='small' onClick={() => setIncludedForSelected(false)}>
                    Exclude
                  </Button>
                </Space.Compact>
                <Space.Compact>
                  <Select
                    size='small'
                    style={{ minWidth: 170 }}
                    placeholder='Set stage…'
                    value={bulkStageId}
                    options={bulkStageOptions}
                    onChange={(v) => setBulkStageId(v as string)}
                  />
                  <Button size='small' disabled={!bulkStageId} onClick={applyBulkStage}>
                    Apply
                  </Button>
                </Space.Compact>
                <Button size='small' onClick={removeSelected}>
                  Remove from list
                </Button>
              </Space>
            )}
            <Table<CandidateRow>
              size='small'
              rowKey='accounts__id'
              columns={columns}
              dataSource={rows}
              scroll={{ x: 940, y: 480 }}
              pagination={false}
              rowSelection={{
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                preserveSelectedRowKeys: true
              }}
            />
          </Card>
        )}

        {watchedTargetFundId && (
          <Card
            title={`Existing prospects — ${targetFundRow?.name || ''} (${fundProspects.length}${fundProspectsHasMore ? '+' : ''})`}
            style={{ marginBottom: 16 }}
          >
            {existingSelectedKeys.length > 0 && (
              <Space style={{ marginBottom: 12 }} wrap>
                <Text strong>{existingSelectedKeys.length} selected:</Text>
                <Space.Compact>
                  <Select
                    size='small'
                    style={{ minWidth: 170 }}
                    placeholder='Set stage…'
                    value={existingBulkStageId}
                    options={bulkStageOptions}
                    onChange={(v) => setExistingBulkStageId(v as string)}
                  />
                  <Button
                    size='small'
                    disabled={!existingBulkStageId}
                    onClick={applyExistingBulkStage}
                  >
                    Apply
                  </Button>
                </Space.Compact>
                <Button size='small' onClick={() => setExistingSelectedKeys([])}>
                  Clear selection
                </Button>
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: deleteSelectedProspects
                      }
                    ]
                  }}
                >
                  <Button size='small' icon={<MoreOutlined />} />
                </Dropdown>
              </Space>
            )}
            <div ref={existingTableWrapRef}>
              <Table<ProspectRecord>
                size='small'
                rowKey={(r: any) => String(r.prospects__id ?? r.id)}
                columns={fundProspectColumns}
                dataSource={fundProspects}
                loading={fundProspectsLoading}
                locale={{ emptyText: 'No prospects in this fund yet.' }}
                scroll={{ y: 400 }}
                pagination={false}
                rowSelection={{
                  selectedRowKeys: existingSelectedKeys,
                  onChange: (keys) => setExistingSelectedKeys(keys),
                  preserveSelectedRowKeys: true,
                  getCheckboxProps: (row: any) => ({
                    disabled: isExistingRowLocked(row) || !row.pipeline_prospects__id
                  })
                }}
              />
            </div>
            {(fundProspectsLoadingMore || fundProspectsHasMore) && (
              <div className='pt-2 text-center'>
                <Text type='secondary'>
                  {fundProspectsLoadingMore
                    ? 'Loading more…'
                    : `Showing ${fundProspects.length} — scroll for more`}
                </Text>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
